import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Alert,
  Stack,
  LinearProgress,
} from '@mui/material';
import {
  Warning,
  Error as ErrorIcon,
  Info as InfoIcon,
  TrendingDown,
  Schedule,
  Block,
  CheckCircle,
} from '@mui/icons-material';
import { Account } from '../types';

interface RiskIndicatorsProps {
  account: Account;
  healthTrend?: any;
  activityHeatmap?: any;
}

interface RiskIndicator {
  severity: 'critical' | 'warning' | 'info' | 'success';
  title: string;
  description: string;
  metric?: string;
  icon: React.ReactNode;
}

export const RiskIndicators: React.FC<RiskIndicatorsProps> = ({
  account,
  healthTrend,
  activityHeatmap,
}) => {
  const analyzeRisks = (): RiskIndicator[] => {
    const risks: RiskIndicator[] = [];

    // 1. Health Score Analysis
    if (account.healthScore < 25) {
      risks.push({
        severity: 'critical',
        title: 'Critical Health Score',
        description: `Health score is ${account.healthScore}, indicating severe risk of churn.`,
        metric: `${account.healthScore}/100`,
        icon: <ErrorIcon />,
      });
    } else if (account.healthScore < 50) {
      risks.push({
        severity: 'warning',
        title: 'Low Health Score',
        description: `Health score is ${account.healthScore}, below the recommended threshold.`,
        metric: `${account.healthScore}/100`,
        icon: <Warning />,
      });
    }

    // 2. Health Trend Analysis
    if (healthTrend?.summary?.trend === 'declining') {
      const change = healthTrend.summary.change;
      risks.push({
        severity: change < -20 ? 'critical' : 'warning',
        title: 'Declining Health Score',
        description: `Health score has decreased by ${Math.abs(change)} points over the last 90 days.`,
        metric: `${change} pts`,
        icon: <TrendingDown />,
      });
    }

    // 3. Stage Analysis
    if (account.onboardingStage === 'churned') {
      risks.push({
        severity: 'critical',
        title: 'Account Churned',
        description: 'Account has been marked as churned. Immediate action required.',
        icon: <Block />,
      });
    } else if (account.onboardingStage === 'at_risk') {
      risks.push({
        severity: 'warning',
        title: 'At-Risk Stage',
        description: 'Account is in the at-risk stage based on low activity patterns.',
        icon: <Warning />,
      });
    }

    // 4. Activity Analysis
    const daysSinceLastActivity = account.daysSinceLastActivity || 0;
    if (daysSinceLastActivity > 30) {
      risks.push({
        severity: 'critical',
        title: 'No Recent Activity',
        description: `No activity detected for ${daysSinceLastActivity} days. Account may be dormant.`,
        metric: `${daysSinceLastActivity} days`,
        icon: <Schedule />,
      });
    } else if (daysSinceLastActivity > 14) {
      risks.push({
        severity: 'warning',
        title: 'Low Recent Activity',
        description: `Last activity was ${daysSinceLastActivity} days ago. Consider engagement outreach.`,
        metric: `${daysSinceLastActivity} days`,
        icon: <Schedule />,
      });
    }

    // 5. Feature Adoption Analysis
    const features = account.features || {};
    const enabledFeatures = Object.values(features).filter((f: any) => f.enabled).length;
    const totalFeatures = Object.keys(features).length;
    const adoptionRate = totalFeatures > 0 ? (enabledFeatures / totalFeatures) * 100 : 0;

    if (totalFeatures > 0 && adoptionRate < 25) {
      risks.push({
        severity: 'warning',
        title: 'Low Feature Adoption',
        description: `Only ${enabledFeatures} of ${totalFeatures} features adopted (${adoptionRate.toFixed(0)}%).`,
        metric: `${enabledFeatures}/${totalFeatures}`,
        icon: <InfoIcon />,
      });
    }

    // 6. Entity Activity Analysis
    const entities = account.entities || {};
    const entitiesWithNoRecentActivity = Object.entries(entities).filter(
      ([_, data]) => (data.daysSinceLast || 0) > 30
    );

    if (entitiesWithNoRecentActivity.length > 0 && Object.keys(entities).length > 0) {
      const entityNames = entitiesWithNoRecentActivity.map(([name]) => name).slice(0, 2);
      risks.push({
        severity: 'info',
        title: 'Stale Entity Activity',
        description: `No recent activity for ${entityNames.join(', ')}${entitiesWithNoRecentActivity.length > 2 ? ', and others' : ''}.`,
        icon: <InfoIcon />,
      });
    }

    // 7. Engagement Streak Analysis
    if (activityHeatmap?.summary?.currentStreak === 0) {
      risks.push({
        severity: 'warning',
        title: 'Engagement Streak Broken',
        description: 'Current activity streak is 0 days. User has stopped daily engagement.',
        metric: '0 days',
        icon: <Schedule />,
      });
    }

    // 8. Subscription Analysis (if available)
    if (account.subscription?.status === 'canceled' || account.subscription?.status === 'expired') {
      risks.push({
        severity: 'critical',
        title: 'Subscription Inactive',
        description: `Subscription status is ${account.subscription.status}. Revenue at risk.`,
        icon: <Block />,
      });
    } else if (account.subscription?.status === 'past_due') {
      risks.push({
        severity: 'warning',
        title: 'Payment Past Due',
        description: 'Subscription payment is past due. Follow up required.',
        icon: <Warning />,
      });
    }

    return risks;
  };

  const risks = analyzeRisks();
  const criticalRisks = risks.filter((r) => r.severity === 'critical');
  const warningRisks = risks.filter((r) => r.severity === 'warning');
  const infoRisks = risks.filter((r) => r.severity === 'info');

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'error';
      case 'warning':
        return 'warning';
      case 'info':
        return 'info';
      case 'success':
        return 'success';
      default:
        return 'default';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <ErrorIcon color="error" fontSize="small" />;
      case 'warning':
        return <Warning color="warning" fontSize="small" />;
      case 'info':
        return <InfoIcon color="info" fontSize="small" />;
      case 'success':
        return <CheckCircle color="success" fontSize="small" />;
      default:
        return <InfoIcon fontSize="small" />;
    }
  };

  const getRiskScore = (): number => {
    // Calculate overall risk score (0-100, higher = more risk)
    let score = 0;
    score += criticalRisks.length * 30;
    score += warningRisks.length * 15;
    score += infoRisks.length * 5;
    return Math.min(score, 100);
  };

  const riskScore = getRiskScore();

  if (risks.length === 0) {
    return (
      <Alert severity="success" icon={<CheckCircle />}>
        <Typography variant="subtitle2" fontWeight={600}>
          No Risk Indicators Detected
        </Typography>
        <Typography variant="body2">
          This account shows healthy engagement patterns and no immediate risk signals.
        </Typography>
      </Alert>
    );
  }

  return (
    <Box>
      {/* Risk Score Summary */}
      <Card sx={{ mb: 2, borderRadius: 2, bgcolor: riskScore > 60 ? 'error.light' : riskScore > 30 ? 'warning.light' : 'info.light', color: 'text.primary' }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Overall Risk Score
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Based on {risks.length} indicator{risks.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
            <Chip
              label={`${riskScore}/100`}
              color={riskScore > 60 ? 'error' : riskScore > 30 ? 'warning' : 'info'}
              sx={{ fontWeight: 700, fontSize: 18, py: 2, px: 1 }}
            />
          </Box>

          <LinearProgress
            variant="determinate"
            value={riskScore}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                bgcolor: riskScore > 60 ? 'error.dark' : riskScore > 30 ? 'warning.dark' : 'info.dark',
              },
            }}
          />

          <Box display="flex" gap={2} mt={2}>
            {criticalRisks.length > 0 && (
              <Chip
                icon={<ErrorIcon />}
                label={`${criticalRisks.length} Critical`}
                color="error"
                size="small"
              />
            )}
            {warningRisks.length > 0 && (
              <Chip
                icon={<Warning />}
                label={`${warningRisks.length} Warning`}
                color="warning"
                size="small"
              />
            )}
            {infoRisks.length > 0 && (
              <Chip
                icon={<InfoIcon />}
                label={`${infoRisks.length} Info`}
                color="info"
                size="small"
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Risk Details */}
      <Stack spacing={1.5}>
        {risks.map((risk, index) => (
          <Alert
            key={index}
            severity={getSeverityColor(risk.severity) as any}
            icon={getSeverityIcon(risk.severity)}
            sx={{ borderRadius: 2 }}
          >
            <Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {risk.title}
                </Typography>
                {risk.metric && (
                  <Chip label={risk.metric} size="small" variant="outlined" />
                )}
              </Box>
              <Typography variant="body2">{risk.description}</Typography>
            </Box>
          </Alert>
        ))}
      </Stack>
    </Box>
  );
};
